import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { ApolloError } from '@apollo/client'
import { Link, useParams } from 'react-router-dom'
import { useDocumentEditorSectionPath } from 'cg/components/documents/useDocumentBasePath'
import { DragHandle } from 'Shared/dragAndDrop'
import { StyledIcon, StyledIconWrapper } from 'Shared/form/iconPickerSelect'
import { Dropdown, Button } from 'react-bootstrap'
import ReactDOM from 'react-dom'
import { TabItem } from 'Shared/tabNav'
import styled from 'styled-components'
import CreateOrUpdateSectionModal from 'Documents/editor/createOrUpdateSectionModal'
import DeleteSectionModal from 'Documents/editor/deleteSectionModal'

const READ_MODE_EDIT = 'EDIT'
const READ_MODE_PUBLISHED = 'PUBLISHED'

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <CustomToggleButton
    ref={ref}
    size="xs"
    variant="tertiary"
    onClick={e => {
      e.preventDefault()
      onClick(e)
    }}
  >
    {children}
  </CustomToggleButton>
))
CustomToggle.displayName = 'CustomToggle'

export default function TabBarTabItem({
  navRoute,
  style,
  themeColor,
  rowData,
  activeTabId,
  isEditable,
  nodes,
  loading,
  duplicateMutation,
  updateMutation,
  deleteMutation,
  // readMode is one of 'EDIT' or 'PUBLISHED'
  readMode = READ_MODE_EDIT
}) {
  const { versionId } = useParams()
  const { itemType, color, icon, id, title, firstSection } = rowData
  const [draggable, setDraggable] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const isActive = String(id) === activeTabId
  let appliedStyles = { padding: 0 }
  if (style) {
    appliedStyles = { ...appliedStyles, ...style }
  }
  const defaultLinkRoute = useDocumentEditorSectionPath(versionId, firstSection?.id)
  let linkRoute = defaultLinkRoute
  if (navRoute) {
    linkRoute = navRoute
  }

  const calculateDragState = useCallback(() => {
    setDraggable(isEditable)
  }, [isEditable])

  useEffect(() => {
    calculateDragState()
  }, [calculateDragState])

  return (
    <TabItem className="me-3" $active={isActive} $themeColor={themeColor} style={appliedStyles}>
      {readMode === READ_MODE_EDIT && (
        <HandleHoverDragHandle $isDraggable={draggable}>
          <i className="fa fa-grip-vertical" />
        </HandleHoverDragHandle>
      )}
      <Link
        to={linkRoute}
        style={{ overflow: 'hidden', padding: `${readMode === READ_MODE_PUBLISHED ? '0 0.75rem' : '0 0 0 0.25rem'}` }}
      >
        {icon && (
          <StyledIconWrapper $inline className="me-1">
            <StyledIcon className={icon} $color={color} />
          </StyledIconWrapper>
        )}
        <span>{title}</span>
      </Link>
      {readMode === READ_MODE_EDIT && (
        <EditWrapper $visible={isDropdownOpen}>
          <Dropdown
            className="inline"
            autoClose
            onToggle={nextShow => {
              setIsDropdownOpen(nextShow)
            }}
            onSelect={eventKey => {
              switch (eventKey) {
                case 'edit':
                  setShowEditModal(true)
                  break
                case 'duplicate':
                  duplicateMutation.mutateOptimistic({
                    variables: {
                      documentVersionId: versionId,
                      sectionId: id,
                      title: `${title}-COPY`,
                      itemType,
                      nextItemId: id
                    }
                  })
                  break
                case 'delete':
                  setShowDeleteModal(true)
                  break
                default:
              }
            }}
          >
            <Dropdown.Toggle as={CustomToggle} />
            {ReactDOM.createPortal(
              <Dropdown.Menu>
                <Dropdown.Item as="button" eventKey="edit">
                  Edit
                </Dropdown.Item>
                <Dropdown.Item as="button" eventKey="duplicate">
                  Duplicate
                </Dropdown.Item>
                <Dropdown.Item as="button" eventKey="delete">
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>,
              document.body
            )}
          </Dropdown>
        </EditWrapper>
      )}
      {showEditModal && <CreateOrUpdateSectionModal section={rowData} mutation={updateMutation} callback={setShowEditModal} />}
      {showDeleteModal && (
        <DeleteSectionModal
          section={rowData}
          activeTabId={activeTabId}
          mutation={deleteMutation}
          nodesLength={nodes.length}
          callback={setShowDeleteModal}
        />
      )}
    </TabItem>
  )
}

TabBarTabItem.propTypes = {
  navRoute: PropTypes.string,
  style: PropTypes.object,
  themeColor: PropTypes.string,
  rowData: PropTypes.object,
  activeTabId: PropTypes.string,
  isEditable: PropTypes.bool,
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      nextItemId: PropTypes.string,
      title: PropTypes.string,
      itemType: PropTypes.oneOf(['group', 'section', 'tab']),
      depth: PropTypes.number,
      icon: PropTypes.string,
      color: PropTypes.string,
      documentVersionId: PropTypes.string,
      groupId: PropTypes.string,
      path: PropTypes.string,
      firstSection: PropTypes.shape({
        id: PropTypes.string
      }),
      excludeFromPublish: PropTypes.bool
    })
  ),
  loading: PropTypes.bool,
  duplicateMutation: PropTypes.shape({
    error: PropTypes.instanceOf(ApolloError),
    mutateOptimistic: PropTypes.func
  }),
  updateMutation: PropTypes.shape({
    error: PropTypes.instanceOf(ApolloError),
    mutateOptimistic: PropTypes.func
  }),
  deleteMutation: PropTypes.shape({
    error: PropTypes.instanceOf(ApolloError),
    mutateOptimistic: PropTypes.func
  }),
  readMode: PropTypes.oneOf([READ_MODE_EDIT, READ_MODE_PUBLISHED])
}

const EditWrapper = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  ${props => (props.$visible ? '' : 'display: none;')}

  ${TabItem}:hover & {
    display: inherit;
  }
`

const HandleHoverDragHandle = styled(DragHandle)`
  ${TabItem}:hover & {
    visibility: ${props => (props.$isDraggable ? 'visible' : 'hidden')};
    cursor: ${props => (props.$isDraggable ? 'grab' : null)};
  }
`

const CustomToggleButton = styled(Button)`
  border-radius: 0.2rem;
  line-height: 1;
  &::after {
    border-bottom: 0;
    border-left: 0.3rem solid #0000;
    border-right: 0.3rem solid #0000;
    border-top: 0.3rem solid;
    content: '';
    display: inline-block;
    vertical-align: 0.255em;
  }
`
